import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth";
import { Toaster } from "sonner";
import Login from "./pages/Login";
import PublicAvailability from "./pages/PublicAvailability";
import AcceptInvite from "./pages/AcceptInvite";
import AppLayout from "./components/AppLayout";
import FleetDashboard from "./pages/FleetDashboard";
import BoatDetail from "./pages/BoatDetail";
import ChecklistFillout from "./pages/ChecklistFillout";
import ChecklistsList from "./pages/ChecklistsList";
import DefectsList from "./pages/DefectsList";
import AdminBookings from "./pages/AdminBookings";
import BookingDetail from "./pages/BookingDetail";
import AdminUsers from "./pages/AdminUsers";
import CustomerPortal from "./pages/CustomerPortal";
import VaskeplanPage from "./pages/VaskeplanPage";
import ProfilePage from "./pages/ProfilePage";
import PrivacyPage from "./pages/PrivacyPage";
import SettingsPage from "./pages/SettingsPage";
import ArchivePage from "./pages/ArchivePage";
import MessagesPage from "./pages/MessagesPage";
import TimesheetPage from "./pages/TimesheetPage";
import LocationsPage from "./pages/LocationsPage";
import SurveysPage from "./pages/SurveysPage";
import PurchasesPage from "./pages/PurchasesPage";
import OperationsDashboard from "./pages/OperationsDashboard";
import IncidentsPage from "./pages/IncidentsPage";
import IncidentDetail from "./pages/IncidentDetail";
import OfficeTvDashboard from "./pages/OfficeTvDashboard";
import CustomerPhotosAdmin from "./pages/CustomerPhotosAdmin";
import CaptainStatusPublic from "./pages/CaptainStatusPublic";
import PendingPaymentsPage from "./pages/PendingPaymentsPage";
import ReceiptsArchivePage from "./pages/ReceiptsArchivePage";
import BoatLiftPublic from "./pages/BoatLiftPublic";
import BoatLiftAdmin from "./pages/BoatLiftAdmin";
import MarketingHome from "./pages/marketing/Home";
import MarketingFleet from "./pages/marketing/Fleet";
import Greenline33 from "./pages/marketing/Greenline33";
import Greenline39 from "./pages/marketing/Greenline39";
import ExperienceCanal from "./pages/marketing/ExperienceCanal";
import ExperienceCoastline from "./pages/marketing/ExperienceCoastline";
import ExperienceAllInOne from "./pages/marketing/ExperienceAllInOne";
import MarketingAbout from "./pages/marketing/About";
import MarketingFaq from "./pages/marketing/Faq";
import MarketingContact from "./pages/marketing/Contact";
import MarketingBook from "./pages/marketing/Book";
import ChatLogPage from "./pages/ChatLogPage";
import VoltTrainingPage from "./pages/VoltTrainingPage";
import AuditLogPage from "./pages/AuditLogPage";
import CookieBanner from "./components/CookieBanner";
import ChatWidget from "./components/ChatWidget";
import { useLocation } from "react-router-dom";
import "./App.css";

function Protected({ roles, children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
        <div className="text-[#A57F39] text-sm uppercase tracking-[0.32em]">Laster…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) {
    if (user.role === "kunde") return <Navigate to="/portal" replace />;
    if (user.role === "admin") return <Navigate to="/admin" replace />;
    if (user.role === "service") return <Navigate to="/service" replace />;
    if (user.role === "vask") return <Navigate to="/vask" replace />;
    return <Navigate to="/login" replace />;
  }
  return children;
}

function RoleHome() {
  const { user, loading } = useAuth();
  if (loading || user === null) return null;
  // Domene-basert: canalboats.no (og evt. www.) skal alltid vise den
  // offentlige marketing-forsiden – uavhengig av innlogging. På alle
  // andre domener (canalboats.me + preview) beholdes den opprinnelige
  // flyten: uinnloggede sendes til /login og innloggede rutes til sitt
  // rolledashbord.
  const host = typeof window !== "undefined" ? window.location.hostname : "";
  const isMarketingDomain = /(^|\.)canalboats\.no$/i.test(host);
  if (isMarketingDomain) return <MarketingHome />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "admin") return <Navigate to="/admin" replace />;
  if (user.role === "service") return <Navigate to="/service" replace />;
  if (user.role === "vask") return <Navigate to="/vask" replace />;
  if (user.role === "kunde") return <Navigate to="/portal" replace />;
  return <Navigate to="/login" replace />;
}


// Global chat-widget: synlig på alle sider UNNTATT marketing-ruter (hvor
// PublicLayout allerede legger den inn selv med riktig i18n). Språk
// bruker vi fra lagret marketing-lang eller "no" som fallback.
function GlobalChatWidget() {
  const location = useLocation();
  const MARKETING = ["/hjem", "/flate", "/baater/", "/opplevelser/", "/om-oss", "/faq", "/kontakt", "/book"];
  const isMarketing = MARKETING.some((p) => location.pathname === p || location.pathname.startsWith(p));
  if (isMarketing) return null;
  let lang = "no";
  try { lang = localStorage.getItem("cbt:marketing_lang") || "no"; } catch { /* noop */ }
  return <ChatWidget lang={lang} page={location.pathname} />;
}

function App() {
  return (
    <AuthProvider>
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <CookieBanner />
        <Routes>
          <Route path="/" element={<RoleHome />} />
          {/* Marketing-nettstedet — synlig på /hjem for forhåndsvisning
              på canalboats.me. Aktiveres automatisk på "/" når appen
              serveres fra canalboats.no (se RoleHome). */}
          <Route path="/hjem" element={<MarketingHome />} />
          <Route path="/flate" element={<MarketingFleet />} />
          <Route path="/baater/greenline-33" element={<Greenline33 />} />
          <Route path="/baater/greenline-39" element={<Greenline39 />} />
          <Route path="/opplevelser/kanal" element={<ExperienceCanal />} />
          <Route path="/opplevelser/kyst" element={<ExperienceCoastline />} />
          <Route path="/opplevelser/alt-i-ett" element={<ExperienceAllInOne />} />
          <Route path="/om-oss" element={<MarketingAbout />} />
          <Route path="/faq" element={<MarketingFaq />} />
          <Route path="/kontakt" element={<MarketingContact />} />
          <Route path="/book" element={<MarketingBook />} />
          <Route path="/login" element={<Login />} />
          <Route path="/accept-invite" element={<AcceptInvite />} />
          <Route path="/personvern" element={<PrivacyPage />} />
          <Route path="/privacy" element={<PrivacyPage lang="en" />} />
          <Route path="/tilgjengelighet" element={<PublicAvailability />} />
          <Route path="/availability" element={<PublicAvailability />} />
          <Route path="/kaptein" element={<CaptainStatusPublic />} />
          <Route path="/captain" element={<CaptainStatusPublic />} />

          {/* Kontorskjerm (Office TV) – no auth wrapper, uses a dedicated
              TV-token that never expires. Stays put behind kiosk screens. */}
          <Route path="/tv-dashboard" element={<OfficeTvDashboard />} />
          <Route path="/skjerm" element={<OfficeTvDashboard />} />

          {/* Profile (workers) – customers manage GDPR inside the portal */}
          <Route
            path="/profile"
            element={
              <Protected roles={["admin", "service", "vask"]}>
                <AppLayout><ProfilePage /></AppLayout>
              </Protected>
            }
          />

          {/* Customer */}
          <Route
            path="/portal"
            element={
              <Protected roles={["kunde"]}>
                <CustomerPortal />
              </Protected>
            }
          />

          {/* Admin */}
          <Route
            path="/admin"
            element={
              <Protected roles={["admin"]}>
                <AppLayout><FleetDashboard basePath="/admin" /></AppLayout>
              </Protected>
            }
          />
          <Route
            path="/admin/boats"
            element={
              <Protected roles={["admin"]}>
                <AppLayout><FleetDashboard basePath="/admin" /></AppLayout>
              </Protected>
            }
          />
          <Route
            path="/admin/boats/:id"
            element={
              <Protected roles={["admin", "service", "vask"]}>
                <AppLayout><BoatDetail /></AppLayout>
              </Protected>
            }
          />
          <Route
            path="/admin/vaskeplan"
            element={
              <Protected roles={["admin"]}>
                <AppLayout><VaskeplanPage /></AppLayout>
              </Protected>
            }
          />
          <Route
            path="/admin/checklists"
            element={
              <Protected roles={["admin"]}>
                <AppLayout><ChecklistsList /></AppLayout>
              </Protected>
            }
          />
          <Route
            path="/admin/defects"
            element={
              <Protected roles={["admin"]}>
                <AppLayout><DefectsList /></AppLayout>
              </Protected>
            }
          />
          <Route
            path="/admin/archive"
            element={
              <Protected roles={["admin"]}>
                <AppLayout><ArchivePage /></AppLayout>
              </Protected>
            }
          />
          <Route
            path="/admin/bookings"
            element={
              <Protected roles={["admin"]}>
                <AppLayout><AdminBookings /></AppLayout>
              </Protected>
            }
          />
          <Route
            path="/admin/bookings/:id"
            element={
              <Protected roles={["admin"]}>
                <AppLayout><BookingDetail /></AppLayout>
              </Protected>
            }
          />
          <Route
            path="/admin/surveys"
            element={
              <Protected roles={["admin", "service"]}>
                <AppLayout><SurveysPage /></AppLayout>
              </Protected>
            }
          />
          <Route
            path="/admin/customer-photos"
            element={
              <Protected roles={["admin", "service"]}>
                <AppLayout><CustomerPhotosAdmin /></AppLayout>
              </Protected>
            }
          />
          <Route
            path="/admin/pending-payments"
            element={
              <Protected roles={["admin"]}>
                <AppLayout><PendingPaymentsPage /></AppLayout>
              </Protected>
            }
          />
          <Route
            path="/admin/receipts"
            element={
              <Protected roles={["admin", "service"]}>
                <AppLayout><ReceiptsArchivePage /></AppLayout>
              </Protected>
            }
          />
          <Route path="/loft" element={<BoatLiftPublic />} />
          <Route
            path="/admin/chat-log"
            element={
              <Protected roles={["admin"]}>
                <ChatLogPage />
              </Protected>
            }
          />
          <Route
            path="/admin/volt-training"
            element={
              <Protected roles={["admin"]}>
                <VoltTrainingPage />
              </Protected>
            }
          />
          <Route
            path="/admin/revisjon"
            element={
              <Protected roles={["admin"]}>
                <AuditLogPage />
              </Protected>
            }
          />
          <Route
            path="/admin/loft"
            element={
              <Protected roles={["admin", "service"]}>
                <AppLayout><BoatLiftAdmin /></AppLayout>
              </Protected>
            }
          />
          <Route
            path="/service/customer-photos"
            element={
              <Protected roles={["admin", "service"]}>
                <AppLayout><CustomerPhotosAdmin /></AppLayout>
              </Protected>
            }
          />
          <Route
            path="/admin/purchases"
            element={
              <Protected roles={["admin", "service", "vask"]}>
                <AppLayout><PurchasesPage /></AppLayout>
              </Protected>
            }
          />
          <Route
            path="/incidents"
            element={
              <Protected roles={["admin", "service", "kunde"]}>
                <AppLayout><IncidentsPage /></AppLayout>
              </Protected>
            }
          />
          <Route
            path="/incidents/:id"
            element={
              <Protected roles={["admin", "service", "kunde"]}>
                <AppLayout><IncidentDetail /></AppLayout>
              </Protected>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <Protected roles={["admin"]}>
                <AppLayout><SettingsPage /></AppLayout>
              </Protected>
            }
          />
          <Route
            path="/admin/users"
            element={
              <Protected roles={["admin"]}>
                <AppLayout><AdminUsers /></AppLayout>
              </Protected>
            }
          />
          <Route
            path="/admin/messages"
            element={
              <Protected roles={["admin"]}>
                <AppLayout><MessagesPage /></AppLayout>
              </Protected>
            }
          />
          <Route
            path="/service/messages"
            element={
              <Protected roles={["service"]}>
                <AppLayout><MessagesPage /></AppLayout>
              </Protected>
            }
          />
          <Route
            path="/vask/messages"
            element={
              <Protected roles={["vask"]}>
                <AppLayout><MessagesPage /></AppLayout>
              </Protected>
            }
          />
          <Route
            path="/admin/timesheet"
            element={
              <Protected roles={["admin"]}>
                <AppLayout><TimesheetPage /></AppLayout>
              </Protected>
            }
          />
          <Route
            path="/admin/locations"
            element={
              <Protected roles={["admin"]}>
                <AppLayout><LocationsPage /></AppLayout>
              </Protected>
            }
          />
          <Route
            path="/service/locations"
            element={
              <Protected roles={["service"]}>
                <AppLayout><LocationsPage /></AppLayout>
              </Protected>
            }
          />
          <Route
            path="/service/timesheet"
            element={
              <Protected roles={["service"]}>
                <AppLayout><TimesheetPage /></AppLayout>
              </Protected>
            }
          />
          <Route
            path="/vask/timesheet"
            element={
              <Protected roles={["vask"]}>
                <AppLayout><TimesheetPage /></AppLayout>
              </Protected>
            }
          />

          {/* Service */}
          <Route
            path="/service"
            element={
              <Protected roles={["service"]}>
                <AppLayout><OperationsDashboard /></AppLayout>
              </Protected>
            }
          />
          <Route
            path="/service/fleet"
            element={
              <Protected roles={["service"]}>
                <AppLayout><FleetDashboard basePath="/admin" /></AppLayout>
              </Protected>
            }
          />
          <Route
            path="/service/vaskeplan"
            element={
              <Protected roles={["service"]}>
                <AppLayout><VaskeplanPage /></AppLayout>
              </Protected>
            }
          />
          <Route
            path="/service/checklists"
            element={
              <Protected roles={["service"]}>
                <AppLayout><ChecklistsList /></AppLayout>
              </Protected>
            }
          />
          <Route
            path="/service/defects"
            element={
              <Protected roles={["service"]}>
                <AppLayout><DefectsList /></AppLayout>
              </Protected>
            }
          />
          <Route
            path="/service/archive"
            element={
              <Protected roles={["service"]}>
                <AppLayout><ArchivePage /></AppLayout>
              </Protected>
            }
          />

          {/* Vask */}
          <Route
            path="/vask"
            element={
              <Protected roles={["vask"]}>
                <AppLayout><OperationsDashboard /></AppLayout>
              </Protected>
            }
          />
          <Route
            path="/vask/plan"
            element={
              <Protected roles={["vask", "admin", "service"]}>
                <AppLayout><VaskeplanPage /></AppLayout>
              </Protected>
            }
          />
          <Route
            path="/vask/checklists"
            element={
              <Protected roles={["vask"]}>
                <AppLayout><ChecklistsList types={["maintenance"]} /></AppLayout>
              </Protected>
            }
          />
          <Route
            path="/vask/defects"
            element={
              <Protected roles={["vask"]}>
                <AppLayout><DefectsList /></AppLayout>
              </Protected>
            }
          />

          {/* Checklist fillout (any worker role) */}
          <Route
            path="/checklist/:id"
            element={
              <Protected roles={["admin", "service", "vask"]}>
                <AppLayout><ChecklistFillout /></AppLayout>
              </Protected>
            }
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <GlobalChatWidget />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
